import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BellRing,
  Building2,
  ChefHat,
  Check,
  CheckCircle2,
  ClipboardList,
  Droplets,
  Instagram,
  Linkedin,
  Martini,
  MessageSquare,
  Search,
  Sparkles,
  Twitter,
  UserCheck,
  UserPlus,
  Utensils,
  Wallet,
} from "lucide-react";

import "./LandingPage.css";

// Animated counter — hooks the IntersectionObserver from inside React so we
// avoid the original page's global window/document scripts.
function useCounters() {
  useEffect(() => {
    const root = document.querySelector(".os-landing");
    if (!root) return;
    const counters = root.querySelectorAll<HTMLElement>(".counter[data-target]");
    const fmt = new Intl.NumberFormat("en-CA");

    const animate = (el: HTMLElement, target: number, duration = 1600) => {
      let start: number | null = null;
      const step = (ts: number) => {
        if (start === null) start = ts;
        const p = Math.min((ts - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt.format(Math.floor(eased * target));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const target = parseInt(
              (e.target as HTMLElement).dataset.target ?? "0",
              10,
            );
            animate(e.target as HTMLElement, target);
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.5 },
    );

    counters.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// Scroll-reveal IO — adds `.in` class to elements with `data-anim` once they
// cross into the viewport. Hero items get revealed immediately so the
// above-the-fold copy isn't blank on first paint.
function useScrollReveal() {
  useEffect(() => {
    const root = document.querySelector(".os-landing");
    if (!root) return;
    const targets = root.querySelectorAll<HTMLElement>("[data-anim]");
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -32px 0px" },
    );
    targets.forEach((el) => obs.observe(el));

    requestAnimationFrame(() => {
      root.querySelectorAll(".hero [data-anim]").forEach((el) => {
        el.classList.add("in");
      });
    });

    return () => obs.disconnect();
  }, []);
}

// Sticky-nav box-shadow toggle once the page scrolls past the fold edge.
function useStickyNavShadow(navRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const onScroll = () => {
      navRef.current?.classList.toggle("scrolled", window.scrollY > 40);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [navRef]);
}

export function LandingPage() {
  const [tab, setTab] = useState<"workers" | "businesses">("workers");
  const [menuOpen, setMenuOpen] = useState(false);
  const [announceVisible, setAnnounceVisible] = useState(true);
  const navRef = useRef<HTMLElement>(null);

  useCounters();
  useScrollReveal();
  useStickyNavShadow(navRef);

  // When the mobile menu is open, lock body scroll the way the brand HTML
  // does. Restoring on close — and on unmount — prevents a stuck `overflow:
  // hidden` if the user navigates away with the menu still open.
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="os-landing">
      {/* MOBILE MENU */}
      <div className={`mobile-menu${menuOpen ? " open" : ""}`} id="mobileMenu">
        <a href="#why" onClick={closeMenu}>Why OneShift</a>
        <a href="#how-it-works" onClick={closeMenu}>How It Works</a>
        <a href="#workers" onClick={closeMenu}>For Workers</a>
        <a href="#businesses" onClick={closeMenu}>For Businesses</a>
        <div className="mm-actions">
          <Link to="/login" className="btn btn-outline-forest btn-sm" onClick={closeMenu}>
            Sign in
          </Link>
          <Link to="/signup" className="btn btn-yellow" onClick={closeMenu}>
            Get Started Free
          </Link>
        </div>
      </div>

      {/* ANNOUNCEMENT BAR */}
      {announceVisible && (
        <div className="announce-bar" id="announceBar">
          <p>
            Ottawa's fastest-growing hospitality platform is now open for businesses.{" "}
            <Link to="/signup">Request early access →</Link>
          </p>
          <button
            className="announce-close"
            aria-label="Dismiss announcement"
            onClick={() => setAnnounceVisible(false)}
          >
            ×
          </button>
        </div>
      )}

      {/* NAV */}
      <nav className="nav" id="nav" ref={navRef}>
        <div className="container">
          <div className="nav-inner">
            <Link to="/" className="logo" aria-label="OneShift">
              <div className="logo-mark">
                <svg viewBox="0 0 20 20" fill="none">
                  <line x1="5" y1="14" x2="14" y2="5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                  <polyline points="9,5 14,5 14,10" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="logo-text">one<span>Shift</span></span>
            </Link>

            <ul className="nav-links">
              <li><a href="#why">Why OneShift</a></li>
              <li><a href="#how-it-works">How It Works</a></li>
              <li><a href="#workers">For Workers</a></li>
              <li><a href="#businesses">For Businesses</a></li>
            </ul>

            <div className="nav-right">
              <Link to="/login" className="nav-login">Sign in</Link>
              <Link to="/signup" className="btn-nav">Get Started</Link>
            </div>

            <button
              className={`hamburger${menuOpen ? " open" : ""}`}
              id="hamburger"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <div className="hero-tag" data-anim>
              <span className="live-dot"></span>
              Now in beta · Ottawa, Ontario
            </div>

            <h1 data-anim data-d="1">
              Ottawa's platform for{" "}
              <span className="kw kw-pink">workers</span>,{" "}
              <span className="kw kw-teal">venues</span>, and{" "}
              <span className="kw kw-yellow">great shifts</span>.
            </h1>

            <p className="hero-sub" data-anim data-d="2">
              Skilled hospitality staff, background-checked and rated, available same-day.
              No agency markup. No middlemen.
            </p>

            <div className="hero-ctas" data-anim data-d="3">
              <Link to="/signup" className="btn btn-yellow">Find Shifts →</Link>
              <Link to="/signup" className="btn btn-outline-white">Post a Shift</Link>
            </div>

            <div className="hero-proof" data-anim data-d="4">
              <div className="proof-badge">
                <div>
                  <div className="pb-num">100%</div>
                  <div className="pb-label">Background checked</div>
                </div>
              </div>
              <div className="pb-divider"></div>
              <div className="proof-badge">
                <div>
                  <div className="pb-num">$0</div>
                  <div className="pb-label">Agency markup</div>
                </div>
              </div>
              <div className="pb-divider"></div>
              <div className="proof-badge">
                <div>
                  <div className="pb-num">&lt;10 min</div>
                  <div className="pb-label">Target time to fill</div>
                </div>
              </div>
            </div>
          </div>

          {/* Animated scene */}
          <div className="hero-scene" aria-hidden="true">
            <span className="scene-particle sp-1"></span>
            <span className="scene-particle sp-2"></span>
            <span className="scene-particle sp-3"></span>
            <span className="scene-particle sp-4"></span>

            <div className="scene-beam"></div>

            {/* 1) Shift posted */}
            <div className="scene-card scene-post">
              <div className="scene-post-head">
                <div className="scene-post-avatar"><Martini size={18} strokeWidth={2.4} aria-hidden /></div>
                <div className="scene-post-meta">
                  <div className="scene-post-venue">The Whalesbone</div>
                  <div className="scene-post-time">Posted 2 min ago</div>
                </div>
                <div className="scene-post-tag">Urgent</div>
              </div>
              <div className="scene-post-title">Bartender — tonight 6pm</div>
              <div className="scene-post-row">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                ByWard Market · 1.2 km
              </div>
              <div className="scene-post-row">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                6:00 pm – 12:00 am · 6 hrs
              </div>
              <div className="scene-post-pay">
                <span className="scene-post-pay-label">Total</span>
                <span className="scene-post-pay-num">$162</span>
              </div>
            </div>

            {/* 2) Workers matching */}
            <div className="scene-card scene-match">
              <div className="scene-match-label">
                <span className="dot"></span> 3 matched · live
              </div>
              <div className="scene-match-list">
                <div className="scene-match-row">
                  <div className="scene-match-avatar ava-1">M</div>
                  <div className="scene-match-info">
                    <div className="scene-match-name">Maya T.</div>
                    <div className="scene-match-stars"><span className="star">★</span> 4.9 · 142 shifts</div>
                  </div>
                  <span className="scene-match-dist">0.8 km</span>
                </div>
                <div className="scene-match-row">
                  <div className="scene-match-avatar ava-2">D</div>
                  <div className="scene-match-info">
                    <div className="scene-match-name">Daniel O.</div>
                    <div className="scene-match-stars"><span className="star">★</span> 4.8 · 96 shifts</div>
                  </div>
                  <span className="scene-match-dist">1.4 km</span>
                </div>
                <div className="scene-match-row">
                  <div className="scene-match-avatar ava-3">S</div>
                  <div className="scene-match-info">
                    <div className="scene-match-name">Sara L.</div>
                    <div className="scene-match-stars"><span className="star">★</span> 5.0 · 58 shifts</div>
                  </div>
                  <span className="scene-match-dist">2.1 km</span>
                </div>
              </div>
            </div>

            {/* 3) Confirmed */}
            <div className="scene-card scene-confirm">
              <div className="scene-confirm-head">
                <div className="scene-confirm-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 12 10 17 19 8" /></svg>
                </div>
                <div>
                  <div className="scene-confirm-title">Shift confirmed</div>
                  <div className="scene-confirm-sub">Filled in 4 minutes</div>
                </div>
              </div>
              <div className="scene-confirm-body">
                <div className="scene-match-avatar ava-1">M</div>
                <div>
                  <div className="scene-confirm-name">Maya is on her way</div>
                  <div className="scene-confirm-eta">ETA 5:48 pm · GPS tracked</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className="trust-strip">
        <div className="container">
          <div className="trust-inner">
            <span className="trust-label">Built for Ottawa hospitality</span>
            <div className="trust-stats">
              <div className="tstat" data-anim>
                <div className="tstat-num">100%</div>
                <div className="tstat-label">Background-checked workers</div>
              </div>
              <div className="tstat-div"></div>
              <div className="tstat" data-anim data-d="1">
                <div className="tstat-num">$0</div>
                <div className="tstat-label">Agency markup</div>
              </div>
              <div className="tstat-div"></div>
              <div className="tstat" data-anim data-d="2">
                <div className="tstat-num">3 hr</div>
                <div className="tstat-label">Minimum shift guaranteed</div>
              </div>
              <div className="tstat-div"></div>
              <div className="tstat" data-anim data-d="3">
                <div className="tstat-num">&lt;10 min</div>
                <div className="tstat-label">Target time to fill</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WHY ONESHIFT */}
      <section className="section bg-cream" id="why">
        <div className="container">
          <div className="why-grid">
            <div className="why-left">
              <div className="why-eyebrow" data-anim>Built for hospitality</div>
              <h2 data-anim>Why OneShift?</h2>
              <p data-anim data-d="1">
                Ottawa's hospitality scene runs on skilled people — bartenders pulling late
                shifts, line cooks covering covers, servers keeping service moving. Getting
                the right person there on time shouldn't take a day.
              </p>
              <p data-anim data-d="2">
                Built specifically for hospitality, OneShift is the only platform where every
                worker is background-checked, Smart Serve certified where required, and rated
                by the venues they've already worked.
              </p>
              <div className="why-cta" data-anim data-d="3">
                <a href="#how-it-works" className="btn btn-forest">See how it works →</a>
              </div>
            </div>
            <div className="roles-grid" data-anim="right">
              <span className="role-pill rp-orange" data-anim data-d="1">
                <Martini size={18} strokeWidth={2.4} aria-hidden />
                Bartender
              </span>
              <span className="role-pill rp-teal" data-anim data-d="2">
                <Utensils size={18} strokeWidth={2.4} aria-hidden />
                Server
              </span>
              <span className="role-pill rp-yellow" data-anim data-d="3">
                <ChefHat size={18} strokeWidth={2.4} aria-hidden />
                Line Cook
              </span>
              <span className="role-pill rp-pink" data-anim data-d="4">
                <BellRing size={18} strokeWidth={2.4} aria-hidden />
                Host
              </span>
              <span className="role-pill rp-mint" data-anim data-d="5">
                <Droplets size={18} strokeWidth={2.4} aria-hidden />
                Dishwasher
              </span>
              <span className="role-pill rp-tan" data-anim data-d="6">
                <Sparkles size={18} strokeWidth={2.4} aria-hidden />
                Busser
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section bg-yellow" id="how-it-works">
        <div className="container">
          <div className="pillars-header">
            <h2 data-anim>Zero to first shift in under ten minutes.</h2>
            <p data-anim data-d="1">No paperwork. No agency. No waiting around. Pick your path.</p>
            <div className="tab-row" data-anim data-d="2">
              <div className="tab-group">
                <button
                  className={`tab-btn${tab === "workers" ? " active" : ""}`}
                  onClick={() => setTab("workers")}
                  type="button"
                >
                  For Workers
                </button>
                <button
                  className={`tab-btn${tab === "businesses" ? " active" : ""}`}
                  onClick={() => setTab("businesses")}
                  type="button"
                >
                  For Businesses
                </button>
              </div>
            </div>
          </div>

          <div className={`tab-pane${tab === "workers" ? " active" : ""}`} id="tab-workers">
            <div className="pillars-grid">
              <div className="pillar-card" data-anim data-d="1">
                <div className="pillar-icon-wrap pi-bg-teal">
                  <UserPlus size={36} strokeWidth={2.2} className="pillar-icon" aria-hidden />
                </div>
                <div className="pillar-step-label">Step 01</div>
                <h3>Create your profile</h3>
                <p>Add your experience, roles you work, Smart Serve cert, and availability. Takes 5 minutes. No CV needed.</p>
                <Link to="/signup" className="pillar-link">Get started →</Link>
              </div>
              <div className="pillar-card" data-anim data-d="2">
                <div className="pillar-icon-wrap pi-bg-orange">
                  <Search size={36} strokeWidth={2.2} className="pillar-icon" aria-hidden />
                </div>
                <div className="pillar-step-label">Step 02</div>
                <h3>Browse and claim shifts</h3>
                <p>See live shifts near you — filter by role, date, location, and pay. Claim the ones that work for you.</p>
                <Link to="/signup" className="pillar-link">Browse shifts →</Link>
              </div>
              <div className="pillar-card" data-anim data-d="3">
                <div className="pillar-icon-wrap pi-bg-mint">
                  <Wallet size={36} strokeWidth={2.2} className="pillar-icon" aria-hidden />
                </div>
                <div className="pillar-step-label">Step 03</div>
                <h3>Show up and get paid</h3>
                <p>GPS check-in confirms your arrival. Finish the shift, get rated, get paid fast. Every good shift builds your reputation.</p>
                <Link to="/signup" className="pillar-link">Learn more →</Link>
              </div>
            </div>
          </div>

          <div className={`tab-pane${tab === "businesses" ? " active" : ""}`} id="tab-businesses">
            <div className="pillars-grid">
              <div className="pillar-card" data-anim data-d="1">
                <div className="pillar-icon-wrap pi-bg-pink">
                  <ClipboardList size={36} strokeWidth={2.2} className="pillar-icon" aria-hidden />
                </div>
                <div className="pillar-step-label">Step 01</div>
                <h3>Post your shift</h3>
                <p>Role, date, hours, pay, location. Mark it urgent if you need someone fast. Two minutes, done.</p>
                <Link to="/signup" className="pillar-link">Post a shift →</Link>
              </div>
              <div className="pillar-card" data-anim data-d="2">
                <div className="pillar-icon-wrap pi-bg-teal">
                  <CheckCircle2 size={36} strokeWidth={2.2} className="pillar-icon" aria-hidden />
                </div>
                <div className="pillar-step-label">Step 02</div>
                <h3>Review and confirm</h3>
                <p>Browse matched workers with background checks and real venue ratings. Pick your person and confirm.</p>
                <Link to="/signup" className="pillar-link">See the platform →</Link>
              </div>
              <div className="pillar-card" data-anim data-d="3">
                <div className="pillar-icon-wrap pi-bg-lilac">
                  <UserCheck size={36} strokeWidth={2.2} className="pillar-icon" aria-hidden />
                </div>
                <div className="pillar-step-label">Step 03</div>
                <h3>They show up. You're covered.</h3>
                <p>GPS confirms arrival. Review GPS-verified timesheets, approve hours — all in one dashboard.</p>
                <Link to="/signup" className="pillar-link">Learn more →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BENTO */}
      <section className="section bg-forest">
        <div className="container">
          <div className="stats-header">
            <h2 data-anim>What we're <span className="kw-yellow">built on</span></h2>
          </div>
          <div className="bento">
            <div className="bento-cell" data-anim data-d="1">
              <div className="bento-num bn-yellow">100%</div>
              <div className="bento-label">Every worker background-checked before their first shift</div>
            </div>
            <div className="bento-cell" data-anim data-d="2">
              <div className="bento-num bn-teal">$0</div>
              <div className="bento-label">Agency markup. You pay the worker directly — nothing on top</div>
            </div>
            <div className="bento-cell" data-anim data-d="3">
              <div className="bento-num bn-pink">3 hr</div>
              <div className="bento-label">Minimum shift length — every worker is guaranteed a fair day's work</div>
            </div>
            <div className="bento-cell" data-anim data-d="4">
              <div className="bento-num bn-mint">Ottawa</div>
              <div className="bento-label">Launching here first. Built for this city's hospitality scene</div>
            </div>
            <div className="bento-cell tall" style={{ gridColumn: 3, gridRow: "1 / span 2" }} data-anim data-d="5">
              <div className="bento-pre">From posting a shift</div>
              <div className="bento-big">
                <div className="bento-num bn-yellow">&lt;10</div>
                <div className="bento-unit">min</div>
              </div>
              <div className="bento-post">to a confirmed worker.<br />Not hours — minutes.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FOR WORKERS */}
      <section className="section bg-white" id="workers">
        <div className="container">
          <div className="feat-split">
            <div className="feat-copy">
              <div className="eyebrow" data-anim>For Workers</div>
              <h2 data-anim data-d="1">Work when you want.<br />Get paid fast.</h2>
              <p data-anim data-d="2">
                You didn't get into hospitality to sit at a desk applying for jobs.{" "}
                <strong>You're skilled, you show up, and you deliver.</strong> OneShift is built
                for Ottawa's cooks, servers, bartenders, and floor staff who want{" "}
                <span className="hl">flexibility without the chaos.</span>
              </p>
              <p data-anim data-d="3">
                Pick up a shift tonight, a weekend gig, or build a steady roster on your terms.
                Every shift you work builds a verified rating that unlocks better opportunities.
              </p>
              <div style={{ marginTop: 28 }} data-anim data-d="4">
                <Link to="/signup" className="btn btn-forest">Find Shifts Near You →</Link>
              </div>
            </div>
            <div className="feat-checklist">
              <div className="feat-check-row" data-anim data-d="1">
                <div className="feat-check-mark"><Check size={16} strokeWidth={3} aria-hidden /></div>
                <div>
                  <h3>Same-day shifts near you</h3>
                  <p>New shifts drop daily. Filter by role, time, and location — work that fits your life.</p>
                </div>
              </div>
              <div className="feat-check-row" data-anim data-d="2">
                <div className="feat-check-mark"><Check size={16} strokeWidth={3} aria-hidden /></div>
                <div>
                  <h3>Transparent pay, locked upfront</h3>
                  <p>See your exact rate before you commit. No agency cut, no last-minute surprises.</p>
                </div>
              </div>
              <div className="feat-check-row" data-anim data-d="3">
                <div className="feat-check-mark"><Check size={16} strokeWidth={3} aria-hidden /></div>
                <div>
                  <h3>Smart Serve & certifications travel with you</h3>
                  <p>Upload once. Your certs surface on every application — no chasing, no resubmitting.</p>
                </div>
              </div>
              <div className="feat-check-row" data-anim data-d="4">
                <div className="feat-check-mark"><Check size={16} strokeWidth={3} aria-hidden /></div>
                <div>
                  <h3>Get paid fast, get rated, get rebooked</h3>
                  <p>Finish a shift, get paid quickly. Every good shift builds a reputation venues can see.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOR BUSINESSES */}
      <section className="section bg-cream" id="businesses">
        <div className="container">
          <div className="feat-split">
            <div className="live-fill-card" data-anim>
              <div className="live-fill-head">
                <span className="live-fill-pulse" aria-hidden></span>
                <span className="live-fill-status">Live fill</span>
              </div>
              <div className="live-fill-title">Bartender · The Whalesbone</div>
              <div className="live-fill-sub">6 hrs · ByWard Market · 1.2 km</div>

              <div className="live-timeline">
                <div className="live-event">
                  <div className="live-event-time">4:02 PM</div>
                  <div className="live-event-label">Shift posted</div>
                </div>
                <div className="live-event">
                  <div className="live-event-time">4:06 PM</div>
                  <div className="live-event-label">5 background-checked applicants</div>
                  <div className="live-event-sub">Smart Serve verified · 0.8–2.1 km away</div>
                </div>
                <div className="live-event live-event--accent">
                  <div className="live-event-time">4:09 PM</div>
                  <div className="live-event-label">Maya T. confirmed</div>
                  <div className="live-event-sub">★ 4.9 · 142 shifts · paid direct, no agency</div>
                </div>
                <div className="live-event">
                  <div className="live-event-time">4:18 PM</div>
                  <div className="live-event-label">On the way · GPS check-in armed</div>
                </div>
              </div>

              <div className="live-fill-foot">
                <span className="live-fill-foot-stat">Filled in 7 minutes</span>
                <span className="live-fill-foot-tag">$0 markup</span>
              </div>
            </div>
            <div className="feat-copy">
              <div className="eyebrow" style={{ color: "var(--orange)" }} data-anim>For Businesses</div>
              <h2 data-anim data-d="1">The right people.<br />When you need them.</h2>
              <p data-anim data-d="2">
                Whether you're covering a sick call at 4pm or scaling for a 500-person gala,{" "}
                <strong>OneShift has experienced hospitality staff ready to step in.</strong>
              </p>
              <p data-anim data-d="3">
                Smart Serve certified where required, background-checked, GPS-tracked on arrival
                — and a platform built to get you sorted fast.{" "}
                <span className="hl">No agency fees. No phone trees. Just the right person, on time.</span>
              </p>
              <div style={{ marginTop: 28 }} data-anim data-d="4">
                <Link to="/signup" className="btn btn-forest">Post a Shift Now →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EARLY ACCESS */}
      <section className="section bg-cream" id="early-access">
        <div className="container">
          <div className="testi-header">
            <h2 data-anim>
              We're just getting{" "}
              <span style={{ color: "var(--forest)" }}>started.</span>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--ink-mid)",
                marginTop: 16,
                lineHeight: 1.7,
              }}
              data-anim
              data-d="1"
            >
              OneShift is in beta in Ottawa. We're onboarding our first workers and venues now —
              the people who'll shape how this platform grows.
            </p>
          </div>
          <div className="testi-grid">
            <div className="t-card" data-anim data-d="1">
              <Martini size={32} strokeWidth={2.2} aria-hidden style={{ color: "var(--forest)", marginBottom: 16 }} />
              <p className="t-quote">
                <strong>For workers:</strong> Be among the first verified hospitality staff on the
                platform. Early members get priority placement and shape the product with direct
                feedback.
              </p>
              <div className="t-footer">
                <div className="t-name">Join as a Worker</div>
                <div className="t-role">
                  Bartenders, Servers, Line Cooks, Hosts, Dishwashers, Bussers
                </div>
              </div>
            </div>
            <div className="t-card" style={{ marginTop: 28 }} data-anim data-d="2">
              <Building2 size={32} strokeWidth={2.2} aria-hidden style={{ color: "var(--forest)", marginBottom: 16 }} />
              <p className="t-quote">
                <strong>For venues:</strong> Ottawa's restaurants, bars, and event spaces get early
                access to Ottawa's first background-checked, on-demand hospitality network. No
                agency. No markup.
              </p>
              <div className="t-footer">
                <div className="t-name">Join as a Business</div>
                <div className="t-role">
                  Restaurants · Bars & Nightclubs · Events & Catering · Hotels
                </div>
              </div>
            </div>
            <div className="t-card" style={{ marginTop: 14 }} data-anim data-d="3">
              <MessageSquare size={32} strokeWidth={2.2} aria-hidden style={{ color: "var(--forest)", marginBottom: 16 }} />
              <p className="t-quote">
                We're building this with the people who'll use it. If you've ever chased shifts on
                WhatsApp or called three agencies at 4pm —{" "}
                <strong>this was built for exactly that moment.</strong>
              </p>
              <div className="t-footer">
                <div className="t-name">Have feedback?</div>
                <div className="t-role">
                  <a
                    href="mailto:hello@oneshift.ca"
                    style={{ color: "var(--forest)", textDecoration: "underline" }}
                  >
                    hello@oneshift.ca
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="section cta-section">
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <h2 data-anim>Ready to run your<br />next <span className="kw-yellow">shift</span>?</h2>
          <p className="cta-sub" data-anim data-d="1">
            No subscriptions. No setup fees. Getting started takes two minutes.
          </p>
          <div className="cta-btns" data-anim data-d="2">
            <Link to="/signup" className="btn btn-yellow">Get Started Free →</Link>
            <a href="#how-it-works" className="btn btn-outline-white">See How It Works</a>
          </div>
          <div className="cta-trust-row" data-anim data-d="3">
            <span className="ct-item">No agency fees</span>
            <span className="ct-item">Background-checked workers</span>
            <span className="ct-item">Same-day payouts</span>
            <span className="ct-item">Hosted in Canada · PIPEDA compliant</span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-logo-row">
                <div className="footer-mark">
                  <svg viewBox="0 0 20 20" fill="none">
                    <line x1="5" y1="14" x2="14" y2="5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    <polyline points="9,5 14,5 14,10" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="footer-brand-name">oneShift</span>
              </div>
              <p className="footer-tagline">Built for the people who make Ottawa's hospitality run.</p>
              <div className="footer-socials">
                <a href="#" className="soc" aria-label="X">
                  <Twitter size={15} strokeWidth={2.2} aria-hidden />
                </a>
                <a href="#" className="soc" aria-label="LinkedIn">
                  <Linkedin size={15} strokeWidth={2.2} aria-hidden />
                </a>
                <a href="#" className="soc" aria-label="Instagram">
                  <Instagram size={15} strokeWidth={2.2} aria-hidden />
                </a>
              </div>
            </div>
            <div className="footer-col">
              <h4>Product</h4>
              <ul className="footer-links">
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="#workers">For Workers</a></li>
                <li><a href="#businesses">For Businesses</a></li>
                <li><a href="#how-it-works">Pricing</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="#why">About</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#">Contact</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <ul className="footer-links">
                <li><a href="#">Help Centre</a></li>
                <li><a href="/legal/privacy.html">Privacy Policy</a></li>
                <li><a href="/legal/terms.html">Terms of Service</a></li>
                <li><a href="#">FAQs</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-legal">
              © 2026 OneShift Inc. · <a href="/legal/privacy.html">Privacy</a> · <a href="/legal/terms.html">Terms</a> · Governing law: Ontario
            </p>
            <span className="footer-badge">Hosted in Canada · PIPEDA compliant</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
